// dwaar/src/pages/Kendra.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Kendra — Sanskrit: केंद्र — center, hub, core.
//   Authenticated dashboard. Shows the organisation's identity, PassPort
//   registry with status badges, and the entry point for new PassPort creation.
//
// Data flow (all queries scoped by RLS to the logged-in org):
//   1. auth.getSession()                             → userId + userEmail
//   2. public.users WHERE id = userId                → gstin
//   3. public.organisations WHERE gstin              → org details
//   4. public.passports WHERE gstin ORDER created_at → passport list
//
// Reveal doctrine:
//   Internal fetch errors stay in the console. The user sees only the generic
//   public error string — no Supabase detail ever surfaces.

import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
// PassPort cards are links to /passport/:id (Samiksha)
import { getSupabaseClient } from "../lib/supabaseClient.js";
import { PASSPORT_LABEL, PUBLIC_ERROR } from "../lib/brand.js";
import Koshtha from "../components/Koshtha.jsx";

// ── Phase ─────────────────────────────────────────────────────────────────────

const PHASE = Object.freeze({
  LOADING: "loading",
  READY:   "ready",
  ERROR:   "error",
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  draft:                "DRAFT",
  pending_confirmation: "PENDING",
  sealed:               "SEALED",
  verified:             "VERIFIED",
};

function formatPeriod(start, end) {
  const fmt = new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" });
  try {
    return (
      fmt.format(new Date(start + "T00:00:00")) +
      " – " +
      fmt.format(new Date(end + "T00:00:00"))
    );
  } catch {
    return `${start} – ${end}`;
  }
}

function capitaliseSector(sector) {
  if (!sector) return "";
  return sector.charAt(0).toUpperCase() + sector.slice(1);
}

// ── PassPort card ─────────────────────────────────────────────────────────────

function PassPortCard({ passport }) {
  const {
    cn_code,
    reporting_period_start,
    reporting_period_end,
    status,
    total_tco2e,
    aps_score,
  } = passport;

  const statusLabel = STATUS_LABELS[status] || status.toUpperCase();
  const co2 = total_tco2e != null ? parseFloat(total_tco2e).toFixed(2) : null;
  const period = formatPeriod(reporting_period_start, reporting_period_end);

  return (
    <Link
      to={`/passport/${passport.id}`}
      style={{ textDecoration: "none", color: "inherit" }}
      aria-label={`${PASSPORT_LABEL} CN ${cn_code} ${statusLabel}`}
    >
      <article className="tl-passport-card">
        <span className={`tl-badge tl-badge--${status}`}>{statusLabel}</span>
        <p className="tl-passport-cn">CN {cn_code}</p>
        <p className="tl-passport-period">{period}</p>
        {co2 && <p className="tl-passport-co2">{co2} tCO₂e</p>}
        {aps_score != null && <p className="tl-passport-aps">APS {aps_score}</p>}
      </article>
    </Link>
  );
}

// ── Kendra ────────────────────────────────────────────────────────────────────

export default function Kendra() {
  const navigate = useNavigate();
  const [phase,           setPhase]           = useState(PHASE.LOADING);
  const [org,             setOrg]             = useState(null);
  const [passports,       setPassports]       = useState([]);
  const [userEmail,       setUserEmail]       = useState("");
  const [accessToken,     setAccessToken]     = useState("");
  const [complianceScope, setComplianceScope] = useState(["CBAM"]);
  const [userName,        setUserName]        = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const client = getSupabaseClient();

        // 1. Session
        const { data: { session } } = await client.auth.getSession();
        if (!session) {
          navigate("/login", { replace: true });
          return;
        }
        const email  = session.user?.email || "";
        const userId = session.user?.id;
        if (!cancelled) setAccessToken(session.access_token || "");

        // 2. User row → gstin
        const { data: userRow, error: userErr } = await client
          .from("users")
          .select("gstin, full_name")
          .eq("id", userId)
          .single();

        if (userErr || !userRow) throw new Error("user-row-missing");

        // 3. Org details + PassPort list — in parallel
        const [orgRes, passRes] = await Promise.all([
          client
            .from("organisations")
            .select("gstin, legal_name, trade_name, sector, compliance_scope")
            .eq("gstin", userRow.gstin)
            .single(),
          client
            .from("passports")
            .select(
              "id, cn_code, reporting_period_start, reporting_period_end, " +
              "status, total_tco2e, emission_intensity, aps_score, created_at"
            )
            .eq("gstin", userRow.gstin)
            .order("created_at", { ascending: false }),
        ]);

        if (orgRes.error) throw new Error("org-missing");

        if (!cancelled) {
          setUserEmail(email);
          setUserName(userRow.full_name || "");
          setOrg(orgRes.data);
          setComplianceScope(orgRes.data.compliance_scope || ["CBAM"]);
          setPassports(passRes.data || []);
          setPhase(PHASE.READY);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("kendra: load failed", err);
        if (!cancelled) setPhase(PHASE.ERROR);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [navigate]);

  // Auto-redirect to /login after sign out
  async function handleSignOut() {
    try {
      await getSupabaseClient().auth.signOut();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("kendra: sign-out error", err);
    } finally {
      navigate("/login", { replace: true });
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (phase === PHASE.LOADING) {
    return (
      <Koshtha role="customer" accessToken={accessToken} complianceScope={complianceScope}>
        <main className="tl-page tl-page--centered">
          <p className="tl-loading">Loading your workspace…</p>
        </main>
      </Koshtha>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (phase === PHASE.ERROR) {
    return (
      <Koshtha role="customer" userEmail={userEmail} userName={userName} accessToken={accessToken} onSignOut={handleSignOut} complianceScope={complianceScope}>
        <main className="tl-page tl-page--centered">
          <p className="tl-error">{PUBLIC_ERROR}</p>
        </main>
      </Koshtha>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────────
  return (
    <Koshtha role="customer" userEmail={userEmail} userName={userName} accessToken={accessToken} onSignOut={handleSignOut} complianceScope={complianceScope}>

      <main className="tl-page">

        {/* Org header */}
        <header className="tl-page-header">
          <h1 className="tl-org-name">{org.legal_name}</h1>
          <p className="tl-org-meta">
            <span className="tl-gstin">{org.gstin}</span>
            <span className="tl-dot" aria-hidden="true">&nbsp;·&nbsp;</span>
            <span
              className="tl-sector-badge"
              aria-label={`Sector: ${capitaliseSector(org.sector)}`}
            >
              {capitaliseSector(org.sector)}
            </span>
            {complianceScope.map((fw) => (
              <React.Fragment key={fw}>
                <span className="tl-dot" aria-hidden="true">&nbsp;·&nbsp;</span>
                <span
                  className={`tl-compliance-badge tl-compliance-badge--${fw.toLowerCase()}`}
                  aria-label={`Compliance: ${fw}`}
                >
                  {fw}
                </span>
              </React.Fragment>
            ))}
          </p>
        </header>

        {/* PassPort section */}
        <section aria-labelledby="kendra-passports-heading">
          <div className="tl-section-toolbar">
            <h2 id="kendra-passports-heading" className="tl-section-heading">
              {PASSPORT_LABEL}s
            </h2>
            <Link
              to="/passport/new"
              className="tl-button-new"
              aria-label={`Create new ${PASSPORT_LABEL}`}
            >
              + New {PASSPORT_LABEL}
            </Link>
            {complianceScope.includes("BRSR") && (
              <Link
                to="/passport/new?type=BRSR"
                className="tl-button-new tl-button-new--brsr"
                aria-label="Create new BRSR Filing"
              >
                + New BRSR Filing
              </Link>
            )}
          </div>

          {passports.length === 0 ? (
            <div className="tl-empty">
              <p className="tl-empty-text">
                No {PASSPORT_LABEL}s yet. Create your first to begin your CBAM
                journey.
              </p>
            </div>
          ) : (
            <div className="tl-passport-grid">
              {passports.map((pp) => (
                <PassPortCard key={pp.id} passport={pp} />
              ))}
            </div>
          )}
        </section>

      </main>
    </Koshtha>
  );
}
